import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  GripHorizontal,
  Loader2,
  Maximize2,
  Minimize2,
  Rocket,
  RefreshCw,
} from 'lucide-react';
import { useOptimizationGenerationToast } from '../context/OptimizationGenerationToastContext';
import PlatformLogo from './PlatformLogo';

/** Below react-hot-toast Toaster (z-index 20000); above modals ~10100 and default content */
const FLOAT_Z_INDEX = 19000;

const BTN_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900';

const PLATFORM_TITLE = {
  google: 'Google Ads',
  meta: 'Meta Ads',
};

function RowIcon({ visual }) {
  if (visual === 'done') {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden={true} />;
  }
  if (visual === 'error') {
    return <AlertCircle className="h-4 w-4 shrink-0 text-red-400" aria-hidden={true} />;
  }
  return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-fuchsia-300" aria-hidden={true} />;
}

const OptimizationGenerationToast = () => {
  const navigate = useNavigate();
  const { state, retryGeneration } = useOptimizationGenerationToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [position, setPosition] = useState(null);

  const containerRef = useRef(null);
  const panelBodyId = useId();

  useEffect(() => {
    if (!state.isVisible) {
      setIsCollapsed(false);
      setPosition(null);
    }
  }, [state.isVisible]);

  const handleDragHandlePointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const originX = position?.x ?? rect.left;
      const originY = position?.y ?? rect.top;
      const startX = e.clientX;
      const startY = e.clientY;

      const onMove = (ev) => {
        let nx = originX + (ev.clientX - startX);
        let ny = originY + (ev.clientY - startY);
        const el = containerRef.current;
        if (el) {
          const w = el.offsetWidth;
          const h = el.offsetHeight;
          const margin = 8;
          nx = Math.max(margin, Math.min(nx, window.innerWidth - w - margin));
          ny = Math.max(margin, Math.min(ny, window.innerHeight - h - margin));
        }
        setPosition({ x: nx, y: ny });
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [position]
  );

  if (!state.isVisible) return null;

  const isPolling = state.status === 'polling';
  const isRunning = isPolling;
  const isError = state.status === 'error';
  const isSuccess = state.status === 'success';
  const isEmpty = state.status === 'empty';
  const progress = Math.max(0, Math.min(100, state.progress || 0));
  const showVerOptimizaciones = isSuccess || isEmpty;

  const activePlatforms = Array.isArray(state.activePlatforms) ? state.activePlatforms : [];
  const hasPerPlatformRows = activePlatforms.length > 0;

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await retryGeneration();
    } finally {
      setIsRetrying(false);
    }
  };

  const wideCard = activePlatforms.length > 1;

  const statusLine =
    isSuccess && state.foundCount > 0
      ? `Encontramos ${state.foundCount} grupo${state.foundCount === 1 ? '' : 's'} activos`
      : isEmpty
        ? 'Revisá la sección Optimizaciones para ver el detalle'
        : isError
          ? 'No se completó la generación'
          : `${Math.round(progress)}% completado`;

  const progressBar = (
    <div
      className={`${isCollapsed ? 'h-1.5' : 'h-2'} w-full overflow-hidden rounded-full bg-white/10`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      aria-label="Progreso estimado de la generación"
    >
      <div
        className={`h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-blue-500 transition-all duration-500 ${
          isPolling ? 'animate-pulse' : ''
        }`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );

  const positionStyle = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto', zIndex: FLOAT_Z_INDEX }
    : { bottom: '1rem', right: '1rem', left: 'auto', top: 'auto', zIndex: FLOAT_Z_INDEX };

  const shellClass = `fixed w-[92vw] ${
    wideCard ? 'max-w-md' : 'max-w-sm'
  } rounded-2xl border border-fuchsia-300/30 bg-slate-900/95 shadow-2xl backdrop-blur-xl select-none ${
    isCollapsed ? 'rounded-xl p-2.5' : 'p-4'
  }`;

  return (
    <div
      ref={containerRef}
      className={shellClass}
      style={positionStyle}
      role="region"
      aria-label={
        isCollapsed
          ? 'Estado de generación de optimizaciones (minimizado)'
          : 'Estado de generación de optimizaciones'
      }
      aria-live="polite"
      aria-busy={isRunning || isRetrying}
    >
      {/* Drag handle: no cerrar; arrastrable para no tapar el flujo de creación manual */}
      <div
        role="group"
        aria-label="Arrastrar panel"
        onPointerDown={handleDragHandlePointerDown}
        className={`mb-2 flex cursor-grab touch-none items-center gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-2 py-1.5 active:cursor-grabbing ${
          isCollapsed ? 'mb-1.5' : 'mb-3'
        }`}
      >
        <GripHorizontal className="h-4 w-4 shrink-0 text-white/40" aria-hidden={true} />
        <span className="sr-only">Arrastrá para mover el panel</span>
        <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-2">
          <Rocket className="h-4 w-4 shrink-0 text-fuchsia-300" aria-hidden={true} />
          <p className="truncate text-sm font-semibold text-white">Optimizaciones IA</p>
        </div>
        {isCollapsed ? (
          <span className="pointer-events-none shrink-0 tabular-nums text-xs text-white/65">
            {Math.round(progress)}%
          </span>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setIsCollapsed((c) => !c)}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white ${BTN_FOCUS}`}
            aria-expanded={!isCollapsed}
            aria-controls={panelBodyId}
            aria-label={isCollapsed ? 'Restaurar panel de optimizaciones' : 'Reducir panel de optimizaciones'}
          >
            {isCollapsed ? (
              <Maximize2 className="h-4 w-4" aria-hidden={true} />
            ) : (
              <Minimize2 className="h-4 w-4" aria-hidden={true} />
            )}
          </button>
        </div>
      </div>

      <div id={panelBodyId}>
      {isCollapsed ? (
        <>
          <div className="mt-1">{progressBar}</div>
          <p className="mt-1.5 truncate text-xs text-white/60" title={statusLine}>
            {statusLine}
          </p>
        </>
      ) : (
        <>
          {hasPerPlatformRows ? (
            <ul className="mb-3 space-y-3">
              {activePlatforms.map((p) => {
                const row = state.rows?.[p];
                if (!row) return null;
                return (
                  <li key={p} className="flex gap-3 rounded-lg border border-white/10 bg-white/5 p-2.5">
                    <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
                      <PlatformLogo plataformas={p} size={20} />
                      <RowIcon visual={row.visual} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                        {PLATFORM_TITLE[p] || p}
                      </p>
                      <p className="mt-0.5 text-sm text-white/90 leading-snug">{row.label}</p>
                      {row.serverError ? (
                        <p className="mt-1 text-xs text-red-300/90" role="alert">
                          {row.serverError}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-white/85">{state.message}</p>
          )}

          {!hasPerPlatformRows && isPolling ? (
            <p className="mt-1 text-xs text-white/55">
              {state.jobServerStatus === 'running' ? 'Procesando en el servidor' : 'En cola'}
            </p>
          ) : null}

          {state.error && !hasPerPlatformRows ? (
            <p className="mt-2 text-xs text-red-300" role="alert">
              {state.error}
            </p>
          ) : null}
          {state.error && hasPerPlatformRows && isError ? (
            <p className="mb-2 text-xs text-red-300" role="alert">
              {state.error}
            </p>
          ) : null}

          <div className="mt-2">{progressBar}</div>

          {isSuccess || isEmpty ? (
            <p className="mt-2 text-sm text-white/85">{state.message}</p>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-white/70">{statusLine}</span>
            <div className="flex flex-wrap items-center gap-2">
              {isError ? (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-fuchsia-600 to-blue-600 px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${BTN_FOCUS}`}
                >
                  {isRetrying ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden={true} />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 shrink-0" aria-hidden={true} />
                  )}
                  {isRetrying ? 'Reintentando…' : 'Reintentar'}
                </button>
              ) : null}
              {showVerOptimizaciones ? (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => navigate('/optimizador')}
                  className={`inline-flex min-h-[44px] items-center justify-center rounded-lg bg-gradient-to-r from-fuchsia-600 to-blue-600 px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 ${BTN_FOCUS}`}
                >
                  Ver optimizaciones
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default OptimizationGenerationToast;
